import { FontProperties } from "shared/datamodel/schemas/text-enabled-schema";
import { NewElementData } from "shared/datamodel/canvas-element";
import consts, { TypeCanvasElement } from "shared/consts";
import MiroJson, {
  MiroGroup,
  MiroWidget,
  MiroWidgetDataJsonFrame,
  MiroWidgetDataJsonPaint,
  MiroWidgetDataJsonShape,
  MiroWidgetDataJsonSticker,
  MiroWidgetDataJsonText,
  MiroWidgetDataType,
  MiroWidgetBasicShapeType,
  MiroWidgetDataJsonImage,
  MiroWidgetDataJsonGrid,
  MiroWidgetDataJsonGridText,
  MiroWidgetDataJsonGridCellStyle,
  MiroWidgetDataJsonGridRoot,
} from "./miro_types";
import { Connector, Drawing, Frame, Shape, StickyNote, TextBlock } from "shared/datamodel/schemas";
import { createElementId, getUnixTimestampUTC } from "shared/util/utils";
import { getDefaultSizeForShape } from "frontend/utils/shape-utils";
import { Fonts } from "../frontend-consts";
import tracking from "frontend/tracking";
import * as MathUtils from "frontend/utils/math-utils";
import closestT from "frontend/geometry/closestT";
import Transform, { Degrees } from "frontend/utils/transform";
import { nanoid } from "nanoid";
import { Sizes } from "frontend/data/shapes/sizes";

const MIRO_DATA_START = /[<|&lt;]--\(miro-data-v1\)/;
const MIRO_DATA_END = /\(\/miro-data-v1\)--[&gt;|>]/;
const FRAME_NAME_PREFIX = "frame-";
const CONNECTOR_NAME_PREFIX = "connector-";
const DEFAULT_BG_COLOR_UNKNOWN_WIDGET = "#A5AAAD";

enum MiroToCanvasTextAlign {
  l = "left",
  c = "center",
  r = "right",
}

enum MiroToCanvasShapeType {
  /* eslint-disable @typescript-eslint/no-duplicate-enum-values */

  // basic shapes
  rectangle = "rect",
  circle = "circle",
  triangle = "triangle",
  bubble = "square-callout",
  roundedRectangle = "rounded-rect",
  rhombus = "diamond",
  parallelogram = "parallelogram",
  star = "pentagram",
  arrowRight = "fat-arrow-right",
  arrowLeft = "fat-arrow-left",
  pentagon = "pentagon",
  hexagon = "hexagon",
  octagon = "octagon",
  trapezoid = "trapezoid",
  arrowLeftRight = "fat-arrow-both",
  cloud = "cloud",
  braceLeft = "left-brace",
  braceRight = "right-brace",
  cross = "cross",
  barrel = "barrel",

  // flowchart
  "flowchart-process" = "rect",
  "flowchart-decision" = "diamond",
  "flowchart-terminator" = "oval",
  "flowchart-predefined-process" = "subroutine",
  "flowchart-document" = "document",
  "flowchart-multiple-documents" = "multidocument",
  "flowchart-manual-input" = "manual-input",
  "flowchart-preparation" = "hexagon",
  "flowchart-data" = "parallelogram",
  "flowchart-database" = "barrel",
  "flowchart-hard-disk" = "sideways-barrel",
  "flowchart-internal-storage" = "internal-storage",
  "flowchart-manual-operation" = "flipped-trapezoid",
  "flowchart-delay" = "delay",
  "flowchart-stored-data" = "datastore",
  "flowchart-merge" = "rounded-triangle-down",
  "flowchart-connector" = "circle",
  "flowchart-or" = "cross-in-circle",
  "flowchart-summing-junction" = "x-in-circle",
  "flowchart-display" = "flowchart-display",
  "flowchart-off-page-link" = "flowchart-offpage-reference",
}

enum MiroToCanvasLineType {
  line = 0,
  elbow = 1,
  curve = 2,
}

export function containsMiroData(clipboardData: string): boolean {
  return MIRO_DATA_START.test(clipboardData);
}

function getMiroData(clipboardFullText: string): string {
  return clipboardFullText?.split(MIRO_DATA_START)[1].split(MIRO_DATA_END)[0];
}

function decodeBase64(encoded: string): string {
  const decoded: string = Buffer.from(encoded, "base64").toString("binary");
  return decoded;
}

function decryptCaesarCipher(encrypted: string): string {
  const charCodes = new Uint8Array(encrypted.length);
  for (let k = 0; k < encrypted.length; k++) {
    charCodes[k] = encrypted.charCodeAt(k);
  }
  const rightShift: number = 123 - charCodes[0];
  for (let c = 0; c < charCodes.length; c++) {
    const k = charCodes[c];
    256 > k && (charCodes[c] = (k + rightShift) % 256);
  }
  const decrypted: string = new TextDecoder().decode(charCodes);
  return decrypted;
}

function getMiroJson(clipboardData: string): MiroJson {
  const miroData: string = getMiroData(clipboardData);
  const miroDataDecoded: string = decodeBase64(miroData);
  const miroDataDecrypted: string = decryptCaesarCipher(miroDataDecoded);

  const miroJson: MiroJson = new MiroJson(JSON.parse(miroDataDecrypted));
  return miroJson;
}

let div: HTMLDivElement | null = null;

export function filterText(miroText: string | null): string | null {
  div ||= document.body.appendChild(document.createElement("div"));
  div.style.opacity = "0";
  div.innerHTML = miroText || "";
  div.id = "miro-text-decypher";
  return div.innerText;
}

export function migrateFontProps(miroTextStyle: any): number {
  let fontProps = 0;
  if (miroTextStyle["b"] == 1) {
    fontProps = fontProps ^ FontProperties.Bold;
  }
  if (miroTextStyle["i"] == 1) {
    fontProps = fontProps ^ FontProperties.Italic;
  }
  if (miroTextStyle["u"] == 1) {
    fontProps = fontProps ^ FontProperties.Underline;
  }
  if (miroTextStyle["s"] == 1) {
    fontProps = fontProps ^ FontProperties.LineThrough;
  }
  return fontProps;
}

export function migrateAlternativeFontProps(miroText: string): number {
  let fontProps = 0;
  if (miroText.includes("</strong>")) {
    fontProps = fontProps ^ FontProperties.Bold;
  }
  if (miroText.includes("</em>")) {
    fontProps = fontProps ^ FontProperties.Italic;
  }
  if (miroText.includes("</u>")) {
    fontProps = fontProps ^ FontProperties.Underline;
  }
  if (miroText.includes("</s>")) {
    fontProps = fontProps ^ FontProperties.LineThrough;
  }
  return fontProps;
}

function componentToHex(c: number): string {
  return c.toString(16).padStart(2, "0");
}

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}
function extractRGB(text: string | null): string {
  if (!text) {
    return "";
  }
  const rgb = text.match(/\d+/g);
  if (rgb) {
    return rgbToHex(parseInt(rgb[0]), parseInt(rgb[1]), parseInt(rgb[2]));
  }
  return "";
}

function migrateFromDecimalColor(color: number, opacity: number = 1): string {
  if (color == -1) {
    return "transparent";
  } else {
    const colorHex =
      color.toString(16).padStart(6, "0") +
      Math.round(opacity * 255)
        .toString(16)
        .padStart(2, "0");
    return "#" + colorHex;
  }
}

function migratePointerStyle(miroPointerType: number): string {
  switch (miroPointerType) {
    case 0: {
      return "none";
    }
    case 1: {
      return "filled-long-arrow";
    }
    case 6: {
      return "wide-arrow";
    }
    case 7: {
      return "arrow";
    }
    case 8: {
      return "filled-wide-arrow";
    }
    case 9: {
      return "filled-rounded-arrow";
    }
    default: {
      return "arrow";
    }
  }
}

function migrateAnchorsMode(x: number, y: number): string {
  if (x == 0 && y == 0.5) {
    return "left";
  } else if (x == 0.5 && y == 0) {
    return "top";
  } else if (x == 1 && y == 0.5) {
    return "right";
  } else if (x == 0.5 && y == 1) {
    return "buttom";
  } else {
    return "outline";
  }
}

// miro line endpoints have x,y that are numbers between 0 and 1.
// they mark the position of the endpoint on a simple shape, (0,0) is top left of its bouding box, (1,1) is bottom right.
// the real bounding box is rotated,scaled and translated, so we need to calculate the real position of the endpoint.
function migrateLinePosition(
  connectorX: number | undefined,
  connectorY: number | undefined,
  elementX: number,
  elementY: number,
  elementHeight: number,
  elementWidth: number,
  scaleX: number,
  scaleY: number,
  rotation = 0
) {
  connectorX ??= 0;
  connectorY ??= 0;
  const transform = new Transform(elementX, elementY, scaleX, scaleY, rotation as Degrees);
  const p = transform.transformPoint({ x: (connectorX - 0.5) * elementWidth, y: (connectorY - 0.5) * elementHeight });
  return p;
}

// miro text x,y are the center of the text and need to be migrated to canvas x,y which top left corner
function migrateTextPosition(
  xCenter: number,
  yCenter: number,
  height: number,
  width: number,
  scaleX: number,
  scaleY: number,
  rotation: number
) {
  const x = xCenter - (width * scaleX) / 2;
  const y = yCenter - (height * scaleY) / 2;
  if (rotation == 0) {
    return {
      x: x,
      y: y,
    };
  } else {
    // need to calculate the new x,y based on the rotation
    const radius = pythagorean(Math.abs(xCenter) - Math.abs(x), Math.abs(yCenter) - Math.abs(y));
    const xNew = radius * Math.cos((rotation * Math.PI) / 180);
    const yNew = radius * Math.sin((rotation * Math.PI) / 180);
    return {
      x: xCenter - xNew,
      y: yCenter - yNew,
    };
  }
}

function migrateInnerPoints(lineType: string, miroLine: any, elementOffset: MathUtils.Point) {
  // Points in element space
  const points = miroLine.points.map((point: MathUtils.Point) => ({
    x: point.x - elementOffset.x,
    y: point.y - elementOffset.y,
  }));

  if (lineType !== "elbow") {
    return points.map((point: MathUtils.Point) => ({ ...point, id: nanoid(10) }));
  }

  const innerPoints = [];
  for (const [index, point] of points.entries()) {
    const next = points[index + 1];
    if (!next) break;

    let orientation;
    if (Math.abs(point.x - next.x) < 1e-3) {
      orientation = "vertical";
    } else if (Math.abs(point.y - next.y) < 1e-3) {
      orientation = "horizontal";
    }
    if (!orientation) break;

    innerPoints.push({
      x: (point.x + next.x) / 2,
      y: (point.y + next.y) / 2,
      orientation,
      id: nanoid(10),
    });
  }

  return innerPoints;
}

function migrateMiroJsonToCanvas(miroJson: MiroJson, mousePosition: MathUtils.Point): NewElementData[] {
  const canvasElements: NewElementData[] = [];

  const groups = miroJson.data.objects.filter((miroWidget) => miroWidget.type == 10);
  const widgetsWithoutGroups = miroJson.data.objects.filter((miroWidget) => !groups.includes(miroWidget));

  //
  // CREATION OF STANDARD ELEMENTS
  //
  const miroCanvasMapper = new Array<any>();
  const failedWidgets = new Map<string, number>();
  const miroLines = new Array<any>();
  for (const [index, miroComponent] of widgetsWithoutGroups.entries()) {
    try {
      const miroWidget = miroComponent as MiroWidget;
      const miroWidgetDataType = MiroWidgetDataType[miroWidget.widgetData.type as keyof typeof MiroWidgetDataType];
      const miroWidgetDataJson = miroWidget.widgetData.json;
      let migratedElement: NewElementData | null = null;
      switch (miroWidgetDataType) {
        case MiroWidgetDataType.frame: {
          console.debug("migrating frame");
          migratedElement = migrateMiroFrame(
            miroWidget.id,
            index,
            miroWidgetDataJson as MiroWidgetDataJsonFrame,
            miroCanvasMapper
          );
          break;
        }
        case MiroWidgetDataType.text: {
          console.debug("migrating text");
          migratedElement = migrateMiroText(
            miroWidget.id,
            index,
            miroWidgetDataJson as MiroWidgetDataJsonText,
            miroCanvasMapper
          );
          break;
        }
        case MiroWidgetDataType.line: {
          console.debug("migrating line");
          miroLines.push({ miroWidget, index });
          break;
        }
        case MiroWidgetDataType.shape:
        case MiroWidgetDataType.stencil: {
          console.debug("migrating shape");
          migratedElement = migrateMiroShape(
            miroWidget.id,
            index,
            miroWidgetDataJson as MiroWidgetDataJsonShape,
            miroCanvasMapper,
            miroWidgetDataType
          );
          break;
        }
        case MiroWidgetDataType.sticker: {
          console.debug("migrating sticker");
          migratedElement = migrateMiroSticker(
            miroWidget.id,
            index,
            miroWidgetDataJson as MiroWidgetDataJsonSticker,
            miroCanvasMapper
          );
          break;
        }
        case MiroWidgetDataType.paint: {
          console.debug("migrating paint");
          migratedElement = migrateMiroPaint(
            miroWidget.id,
            index,
            miroWidgetDataJson as MiroWidgetDataJsonPaint,
            miroCanvasMapper
          );
          break;
        }
        case MiroWidgetDataType.image: {
          console.debug("migrating image into frame"); // image migration is hard, so we will migrate it into frame for now
          migratedElement = migrateMiroImage(
            miroWidget.id,
            index,
            miroWidgetDataJson as MiroWidgetDataJsonImage,
            miroCanvasMapper
          );
          break;
        }
        case MiroWidgetDataType.grid: {
          console.debug("migrating grid into frame"); // grid migration is hard, so we will migrate it into frame for now
          migratedElement = migrateMiroGrid(
            miroWidget.id,
            index,
            miroWidgetDataJson as MiroWidgetDataJsonGrid,
            miroCanvasMapper
          );
          break;
        }
        case MiroWidgetDataType.grid_text: {
          console.debug("migrating grid text into text"); // grid text not supported, so we will migrate it into text for now
          const gridText = miroWidgetDataJson as MiroWidgetDataJsonGridText;
          const grid = widgetsWithoutGroups[gridText._parent.index] as MiroWidget;
          const miroGridDataJson = grid.widgetData.json as MiroWidgetDataJsonGrid;

          migratedElement = migrateMiroGridText(
            miroWidget.id,
            index,
            gridText,
            miroCanvasMapper,
            miroGridDataJson.gridRoot
          );
          break;
        }
        case MiroWidgetDataType.emoji: {
          console.debug(`not implemented type: ${miroWidget.widgetData.type}`);
          // TODO: our text elements can draw emojis just fine...
          throw new Error(`not implemented type: ${miroWidget.widgetData.type}`);
        }
        default: {
          console.debug(`not known type: ${miroWidget.widgetData.type}`);
          throw new Error(`not known type: ${miroWidget.widgetData.type}`);
        }
      }

      // handle grid items positions
      if (miroWidgetDataJson?._position?.column && miroWidgetDataJson?._position?.row) {
        const grid = widgetsWithoutGroups[miroWidgetDataJson._parent.index] as MiroWidget;
        const miroGridDataJson = grid.widgetData.json as MiroWidgetDataJsonGrid;

        const columnIndex = miroGridDataJson.gridRoot.columnsOrder.indexOf(miroWidgetDataJson._position.column);
        const columnsMap = new Map(Object.entries(miroGridDataJson.gridRoot.columns));
        for (let i = 0; i <= columnIndex; i++) {
          const widthSize = columnsMap.get(miroGridDataJson.gridRoot.columnsOrder[i])?.size;
          if (widthSize) {
            if (i == columnIndex) {
              miroWidgetDataJson._position.offsetPx.x += widthSize / 2;
            } else {
              miroWidgetDataJson._position.offsetPx.x += widthSize + 40;
            }
          }
        }

        const rowIndex = miroGridDataJson.gridRoot.rowsOrder.indexOf(miroWidgetDataJson._position.row);
        const rowMap = new Map(Object.entries(miroGridDataJson.gridRoot.rows));
        for (let j = 0; j <= rowIndex; j++) {
          const heightSize = rowMap.get(miroGridDataJson.gridRoot.rowsOrder[j])?.size;
          if (heightSize) {
            if (j == rowIndex) {
              miroWidgetDataJson._position.offsetPx.y += heightSize / 2;
            } else {
              miroWidgetDataJson._position.offsetPx.y += heightSize + 40;
            }
          }
        }

        if (migratedElement) {
          migratedElement.element.x = miroWidgetDataJson?._position.offsetPx.x - miroWidgetDataJson.size.width / 2;
          migratedElement.element.y = miroWidgetDataJson?._position.offsetPx.y - miroWidgetDataJson.size.height / 2;
        }
      }
      if (migratedElement) {
        canvasElements.push(migratedElement);
      }
    } catch (error) {
      console.error(`Error migrating widget: ${miroComponent}`);
      console.error(error);

      // aggregate failed widgets
      const miroWidgetType = (miroComponent as MiroWidget).widgetData.type;
      if (miroWidgetType) {
        const miroWidgetTypeCounter = failedWidgets.get(miroWidgetType);
        miroWidgetTypeCounter
          ? failedWidgets.set(miroWidgetType, 1 + miroWidgetTypeCounter)
          : failedWidgets.set(miroWidgetType, 1);
      }
    }
  }

  // Send failed widgets to tracking
  failedWidgets.forEach((value, key) => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.MIRO_MIGRATION, `failed_widgets_summary`, key, value);
  });

  //
  // POST PROCESSING
  //
  // Set canvasElement x,y - relative to mouse position
  for (const canvasElement of canvasElements) {
    canvasElement.element.x += mousePosition.x;
    canvasElement.element.y += mousePosition.y;
  }

  if (miroLines.length > 0) {
    const elementType = consts.CANVAS_ELEMENTS.CONNECTOR;
    for (const { miroWidget, index } of miroLines) {
      const miroLine = miroWidget.widgetData.json;
      const nanoId = createElementId();
      const miroLineStyle = JSON.parse(miroLine.style);
      const miroLineText = miroLine.line.captions.map((caption: any) => caption.text).join(" ");
      const text = filterText(miroLineText);
      const primaryAnchorMode =
        miroLine.primary.widgetIndex != -1
          ? migrateAnchorsMode(miroLine.primary.point?.x, miroLine.primary.point?.y)
          : "n/a";
      const secondaryAnchorMode =
        miroLine.secondary.widgetIndex != -1
          ? migrateAnchorsMode(miroLine.secondary.point?.x, miroLine.secondary.point?.y)
          : "n/a";

      let point1_t = 0;
      let point2_t = 0;
      let point1 = { x: 0, y: 0 };
      let point2 = { x: 0, y: 0 };
      let element_xy = { x: 0, y: 0 };
      const connectedShapes: any[] = [null, null];

      if (miroLine.primary.widgetIndex == -1) {
        point1 = { x: miroLine.primary.point?.x + mousePosition.x, y: miroLine.primary.point?.y + mousePosition.y };
      } else {
        const canvasElementId = miroCanvasMapper.find(
          (canvasElement) => canvasElement["miroIndex"] == miroLine.primary.widgetIndex
        )?.id;
        const canvasElement = canvasElements.find((element) => element.id == canvasElementId);
        if (canvasElement) {
          connectedShapes[0] = { id: `${canvasElement.type}-` + canvasElement.id, type: canvasElement.type };
          canvasElement.element.attachedConnectors[CONNECTOR_NAME_PREFIX + nanoId] = {
            lineId: CONNECTOR_NAME_PREFIX + nanoId,
          };
          const point1_in_canvas_space = migrateLinePosition(
            miroLine.primary.point?.x,
            miroLine.primary.point?.y,
            canvasElement.element.x,
            canvasElement.element.y,
            canvasElement.element.height,
            canvasElement.element.width,
            canvasElement.element.scaleX,
            canvasElement.element.scaleY,
            canvasElement.element.rotate
          );
          point1 = point1_in_canvas_space; // dont add mouse-position, it was added to canvasElement
          const t = closestT(canvasElement.type as TypeCanvasElement, canvasElement.element)(point1_in_canvas_space);
          point1_t = t.t;
          element_xy = { x: canvasElement.element.x, y: canvasElement.element.y };
        }
      }
      if (miroLine.secondary.widgetIndex == -1) {
        point2 = { x: miroLine.secondary.point?.x + mousePosition.x, y: miroLine.secondary.point?.y + mousePosition.y };
      } else {
        const canvasElementId = miroCanvasMapper.find(
          (canvasElement) => canvasElement["miroIndex"] == miroLine.secondary.widgetIndex
        )?.id;
        const canvasElement = canvasElements.find((element) => element.id == canvasElementId);
        if (canvasElement) {
          connectedShapes[1] = { id: `${canvasElement.type}-` + canvasElement.id, type: canvasElement.type };
          canvasElement.element.attachedConnectors[CONNECTOR_NAME_PREFIX + nanoId] = {
            lineId: CONNECTOR_NAME_PREFIX + nanoId,
          };
          const point2_in_canvas_space = migrateLinePosition(
            miroLine.secondary.point?.x,
            miroLine.secondary.point?.y,
            canvasElement.element.x,
            canvasElement.element.y,
            canvasElement.element.height,
            canvasElement.element.width,
            canvasElement.element.scaleX,
            canvasElement.element.scaleY,
            canvasElement.element.rotate
          );
          point2 = point2_in_canvas_space; // dont add mouse-position, it was added to canvasElement
          const t = closestT(canvasElement.type as TypeCanvasElement, canvasElement.element)(point2_in_canvas_space);
          point2_t = t.t;
        }
      }

      // For inner poionts, we need to find the coords in element space.
      // To find them we can transform the points to element space and then subtract the element position
      const elementPoint1 = { x: point1.x - mousePosition.x, y: point1.y - mousePosition.y };
      const elementPoint2 = { x: point2.x - mousePosition.x, y: point2.y - mousePosition.y };
      const elementOffset = { x: (elementPoint1.x + elementPoint2.x) / 2, y: (elementPoint1.y + elementPoint2.y) / 2 };

      // dont add mouse-position, since point1 and point2 include it already
      element_xy = { x: (point1.x + point2.x) / 2, y: (point1.y + point2.y) / 2 };
      point1.x -= element_xy.x;
      point1.y -= element_xy.y;
      point2.x -= element_xy.x;
      point2.y -= element_xy.y;

      miroCanvasMapper.push({
        id: nanoId,
        miroId: miroWidget.id,
        miroIndex: index,
        miroFrameIndex: miroLine._parent?.index,
        miroPrimaryWidgetIndex: miroLine.primary.widgetIndex,
        miroSecondaryWidgetIndex: miroLine.secondary.widgetIndex,
      });

      const lineType = MiroToCanvasLineType[miroLineStyle.lt];
      const innerPoints = migrateInnerPoints(lineType, miroLine, elementOffset);

      const newElementData: NewElementData = {
        id: nanoId,
        type: elementType,
        element: {
          connectedShapes,
          anchorIndexes: [] as number[],
          type: elementType,
          x: element_xy.x,
          y: element_xy.y,
          rotate: 0,
          points: [point1, point2],
          innerPoints,
          anchorMode: null,
          anchorsMode: [primaryAnchorMode, secondaryAnchorMode],
          anchorOrientation: null,
          activeAnchorIndex: null,
          point1_t,
          point2_t,
          lineType,
          pointerStyles: [migratePointerStyle(miroLineStyle.a_end), migratePointerStyle(miroLineStyle.a_start)],
          stroke: migrateFromDecimalColor(miroLineStyle.lc),
          strokeWidth: miroLineStyle.t,
          dash: Math.abs(miroLineStyle.ls - 2),
          text: text,
          link: getLinkInATag(miroLineText),
          textColor: text ? migrateFromDecimalColor(miroLine.line.captions[0]["color"]) : null,
          fontSize: text ? miroLine.line.captions[0]["fontSize"] : null,
          cursorPosition: 0,
        } as Connector,
      };

      canvasElements.push(newElementData);
    }
  }

  // setting z-index for all elements - calling getUnixTimestampUTC() for each element is not enough because elements have the same timestamp
  let zIndex = getUnixTimestampUTC();
  for (const element of canvasElements) {
    element.element.zIndexLastChangeTime = zIndex;
    zIndex += 1;
  }

  // setting frameId
  for (const canvasElement of canvasElements) {
    // Setting frameId
    const miroFrameIndex = miroCanvasMapper.find((currentElement) => currentElement.id == canvasElement.id)[
      "miroFrameIndex"
    ];
    if (miroFrameIndex != null) {
      const canvasFrameId = miroCanvasMapper.find((frameElement) => frameElement["miroIndex"] == miroFrameIndex)?.id;
      canvasElement.element["frameId"] = FRAME_NAME_PREFIX + canvasFrameId; // need to prefix with "frame-"
    }
  }

  // Set canvasElement x,y - relative to frame if frame exists
  const canvasElementsWithoutConnectors = canvasElements.filter(
    (canvasElement) => canvasElement.type != consts.CANVAS_ELEMENTS.CONNECTOR
  );
  for (const canvasElement of canvasElementsWithoutConnectors) {
    if (canvasElement.element.frameId) {
      const frame = canvasElements.find((element) => FRAME_NAME_PREFIX + element.id == canvasElement.element.frameId);
      if (frame) {
        // remove the mousePosition offset we added, since it was added to the frame xy, we don't want
        // to add it twice
        canvasElement.element.x += frame.element.x - mousePosition.x;
        canvasElement.element.y += frame.element.y - mousePosition.y;
      }
    }
  }

  // migrating groups
  for (const group of groups) {
    const miroGroup = group as MiroGroup;
    const nanoId = createElementId();
    for (const item of miroGroup.items) {
      const canvasElementId = miroCanvasMapper.find((currentElement) => currentElement.miroId == item)?.id;
      const canvasElement = canvasElements.find((element) => element.id == canvasElementId);
      if (canvasElement) {
        canvasElement.element.groupId = nanoId;
      }
    }
  }

  return canvasElements;
}

function getCanvasShapeType(
  miroShape: MiroWidgetDataJsonShape,
  miroShapeType: MiroWidgetDataType
): MiroToCanvasShapeType | null {
  let shapeType = null;
  switch (miroShapeType) {
    case MiroWidgetDataType.shape: {
      shapeType =
        MiroToCanvasShapeType[MiroWidgetBasicShapeType[+miroShape.shape] as keyof typeof MiroToCanvasShapeType] ??
        MiroToCanvasShapeType.rectangle;
      break;
    }
    case MiroWidgetDataType.stencil: {
      const dnShapeType = miroShape["ns:diagrammingNotation"]?.element ?? miroShape["schema"].id;
      shapeType = MiroToCanvasShapeType[dnShapeType as keyof typeof MiroToCanvasShapeType];
      break;
    }
  }
  return shapeType;
}

function getShapeText(miroShape: MiroWidgetDataJsonShape, miroShapeType: MiroWidgetDataType): string | null {
  switch (miroShapeType) {
    case MiroWidgetDataType.shape: {
      return miroShape.text;
    }
    case MiroWidgetDataType.stencil: {
      return miroShape?.schema?.data?.texts?.name?.text;
    }
    default: {
      return null;
    }
  }
}

function getFont(miroFont: string): string {
  if (!miroFont) {
    return consts.DEFAULTS.FONT;
  }
  const miroFontStandardize = miroFont.toLowerCase().replaceAll(" ", "");
  const fontFoundIndex = Fonts.map((f) => f.toLowerCase().replaceAll(" ", "")).indexOf(miroFontStandardize);
  if (fontFoundIndex != -1) {
    return Fonts[fontFoundIndex];
  } else {
    return consts.DEFAULTS.FONT;
  }
}

function getLinkInATag(miroText: string | null): string | null {
  if (!miroText) {
    return null;
  }
  const aIndex = miroText.indexOf("<a");
  if (aIndex == -1) {
    return null;
  }

  const linkStart = aIndex + 9; // +7 to remove <a href="
  miroText = miroText.substring(linkStart, miroText.length);
  return filterText(miroText.substring(0, miroText.indexOf('"')));
}

function migrateMiroFrame(
  miroId: number,
  miroIndex: number,
  miroFrame: MiroWidgetDataJsonFrame,
  miroCanvasMapper: Array<any>
): NewElementData | null {
  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.FRAME;
  miroCanvasMapper.push({ id: nanoId, miroId: miroId, miroIndex: miroIndex });
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      x: miroFrame.x - (miroFrame.size.width * miroFrame.scale.scale) / 2,
      y: miroFrame.y - (miroFrame.size.height * miroFrame.scale.scale) / 2,
      width: miroFrame.width,
      height: miroFrame.height,
      title: filterText(miroFrame.text),
      link: getLinkInATag(miroFrame.text),
      fill: migrateFromDecimalColor(JSON.parse(miroFrame.style).bc),
      scaleX: miroFrame.scale.scale,
      scaleY: miroFrame.scale.scale,
      attachedConnectors: {},
    } as Frame,
  };
}

function migrateMiroImage(
  miroId: number,
  miroIndex: number,
  miroImage: MiroWidgetDataJsonImage,
  miroCanvasMapper: Array<any>
): NewElementData | null {
  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.FRAME;
  miroCanvasMapper.push({ id: nanoId, miroId: miroId, miroIndex: miroIndex, miroFrameIndex: miroImage._parent?.index });
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      x: miroImage._position.offsetPx.x - (miroImage.resource.width * miroImage.scale.scale) / 2,
      y: miroImage._position.offsetPx.y - (miroImage.resource.height * miroImage.scale.scale) / 2,
      width: miroImage.resource.width,
      height: miroImage.resource.height,
      title: "Image placeholder",
      fill: DEFAULT_BG_COLOR_UNKNOWN_WIDGET,
      scaleX: miroImage.scale.scale,
      scaleY: miroImage.scale.scale,
      attachedConnectors: {},
    } as Frame,
  };
}

function migrateMiroGrid(
  miroId: number,
  miroIndex: number,
  miroGrid: MiroWidgetDataJsonGrid,
  miroCanvasMapper: Array<any>
): NewElementData | null {
  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.FRAME;
  miroCanvasMapper.push({ id: nanoId, miroId: miroId, miroIndex: miroIndex, miroFrameIndex: miroGrid._parent?.index });
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      x: miroGrid._position.offsetPx.x - (miroGrid.size.width * miroGrid.scale.scale) / 2,
      y: miroGrid._position.offsetPx.y - (miroGrid.size.height * miroGrid.scale.scale) / 2,
      width: miroGrid.size.width,
      height: miroGrid.size.height,
      title: "Grid placeholder",
      fill: DEFAULT_BG_COLOR_UNKNOWN_WIDGET,
      scaleX: miroGrid.scale.scale,
      scaleY: miroGrid.scale.scale,
      attachedConnectors: {},
    } as Frame,
  };
}

function migrateMiroGridText(
  miroId: number,
  miroIndex: number,
  miroGridText: MiroWidgetDataJsonGridText,
  miroCanvasMapper: Array<any>,
  gridRoot: MiroWidgetDataJsonGridRoot
): NewElementData | null {
  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.TEXT_BLOCK;
  const cellStylesMap = new Map(
    Object.entries(gridRoot.cellStyles).map(([key, value]: [string, any]) => [
      key,
      new Map(
        Object.entries(value).map(([key, value]: [string, any]) => [key, new MiroWidgetDataJsonGridCellStyle(value)])
      ),
    ])
  );
  const cellStyle =
    cellStylesMap.get(miroGridText._position.column)?.get(miroGridText._position.row) ??
    new MiroWidgetDataJsonGridCellStyle({
      textColor: consts.DEFAULTS.TEXT_COLOR,
      fontSize: Sizes.get().stickyNote.fontSize,
      textAlign: MiroToCanvasTextAlign.c,
    });

  miroCanvasMapper.push({
    id: nanoId,
    miroId: miroId,
    miroIndex: miroIndex,
    miroFrameIndex: miroGridText._parent?.index,
  });
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      x: miroGridText._position.offsetPx.x,
      y: miroGridText._position.offsetPx.y,
      text: filterText(miroGridText.html),
      link: getLinkInATag(miroGridText.html),
      textColor: cellStyle.textColor ?? consts.DEFAULTS.TEXT_COLOR,
      fontSize: cellStyle.fontSize,
      font: getFont(gridRoot.style.fontFamily),
      align: cellStyle.textAlign ?? MiroToCanvasTextAlign.c,
      fontProps: migrateAlternativeFontProps(miroGridText.html),
      width: miroGridText.size.width * miroGridText.scale.scale,
      height: miroGridText.size.height * miroGridText.scale.scale,
      scaleX: miroGridText.scale.scale,
      scaleY: miroGridText.scale.scale,
      rotate: miroGridText.rotation.rotation,
      attachedConnectors: {},
      textPosition: { x: 0, y: 0 },
    } as TextBlock,
  };
}

function pythagorean(sideA: number, sideB: number) {
  // Use the Pythagorean theorem to calculate the length of the hypotenuse.
  return Math.sqrt(Math.pow(sideA, 2) + Math.pow(sideB, 2));
}

function migrateMiroText(
  miroId: number,
  miroIndex: number,
  miroText: MiroWidgetDataJsonText,
  miroCanvasMapper: Array<any>
): NewElementData | null {
  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.TEXT_BLOCK;
  const miroTextStyle = JSON.parse(miroText.style);
  const textPosition = migrateTextPosition(
    miroText._position.offsetPx.x,
    miroText._position.offsetPx.y,
    miroText.size.height,
    miroText.size.width,
    miroText.scale.scale,
    miroText.scale.scale,
    miroText.rotation.rotation
  );

  miroCanvasMapper.push({ id: nanoId, miroId: miroId, miroIndex: miroIndex, miroFrameIndex: miroText._parent?.index });
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      x: textPosition.x,
      y: textPosition.y,
      text: filterText(miroText.text),
      link: getLinkInATag(miroText.text),
      textColor: migrateFromDecimalColor(miroTextStyle.tc),
      fontSize: miroTextStyle.st,
      font: getFont(miroTextStyle.ffn),
      align: MiroToCanvasTextAlign[miroTextStyle.ta as keyof typeof MiroToCanvasTextAlign],
      fontProps: migrateFontProps(miroTextStyle),
      width: miroText.size.width,
      height: miroText.size.height,
      scaleX: miroText.scale.scale,
      scaleY: miroText.scale.scale,
      rotate: miroText.rotation.rotation,
      attachedConnectors: {},
      textPosition: { x: 0, y: 0 },
    } as TextBlock,
  };
}

function migrateMiroSticker(
  miroId: number,
  miroIndex: number,
  miroSticker: MiroWidgetDataJsonSticker,
  miroCanvasMapper: Array<any>
): NewElementData | null {
  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.STICKY_NOTE;
  const miroStickerStyle = JSON.parse(miroSticker.style);
  miroCanvasMapper.push({
    id: nanoId,
    miroId: miroId,
    miroIndex: miroIndex,
    miroFrameIndex: miroSticker._parent?.index,
  });
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      x: miroSticker._position.offsetPx.x - (miroSticker.size.width * miroSticker.scale.scale) / 2,
      y: miroSticker._position.offsetPx.y - (miroSticker.size.height * miroSticker.scale.scale) / 2,
      width: miroSticker.size.width,
      height: miroSticker.size.height - miroSticker.size.height / 5,
      fill: migrateFromDecimalColor(miroStickerStyle.sbc),
      scaleX: miroSticker.scale.scale,
      scaleY: miroSticker.scale.scale,
      attachedConnectors: {},
      text: filterText(miroSticker.text),
      link: getLinkInATag(miroSticker.text),
      // old logic - miroStickerStyle.fs != 0 ? miroStickerStyle.fs : consts.DEFAULTS.STICKY_NOTE_FONT_SIZE * 2,
      fontSize: "auto",
      font: getFont(miroStickerStyle.ffn),
      cursorPosition: 0,
      fontProps: migrateAlternativeFontProps(miroSticker.text),
      align: MiroToCanvasTextAlign[miroStickerStyle.ta as keyof typeof MiroToCanvasTextAlign],
      isWide: false,
    } as StickyNote,
  };
}

function migrateMiroPaint(
  miroId: number,
  miroIndex: number,
  miroPaint: MiroWidgetDataJsonPaint,
  miroCanvasMapper: Array<any>
): NewElementData | null {
  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.DRAWING;
  const miroPaintStyle = JSON.parse(miroPaint.style);
  miroCanvasMapper.push({ id: nanoId, miroId: miroId, miroIndex: miroIndex, miroFrameIndex: miroPaint._parent?.index });
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      x: miroPaint._position.offsetPx.x - (miroPaint.size.width * miroPaint.scale.scale) / 2,
      y: miroPaint._position.offsetPx.y - (miroPaint.size.height * miroPaint.scale.scale) / 2,
      points: miroPaint.points.map((point: any) => [point.x, point.y]).flat(),
      stroke: migrateFromDecimalColor(miroPaintStyle.lc),
      strokeWidth: miroPaintStyle.t,
      rotate: miroPaint.rotation.rotation,
      scaleX: miroPaint.scale.scale,
      scaleY: miroPaint.scale.scale,
      attachedConnectors: {},
    } as Drawing,
  };
}

function migrateMiroShape(
  miroId: number,
  miroIndex: number,
  miroShape: MiroWidgetDataJsonShape,
  miroCanvasMapper: Array<any>,
  miroWidgetDataType: MiroWidgetDataType
): NewElementData | null {
  let canvasShapeType = getCanvasShapeType(miroShape, miroWidgetDataType);
  let shapeText = getShapeText(miroShape, miroWidgetDataType);
  const miroShapeStyle = JSON.parse(miroShape.style);
  const textColor = miroShape.schema?.data?.texts?.name?.text
    ? extractRGB(miroShape.schema?.data?.texts?.name?.text)
    : migrateFromDecimalColor(miroShapeStyle.tc);
  let bgColor = migrateFromDecimalColor(miroShapeStyle.bc, miroShapeStyle.bo);
  if (!canvasShapeType) {
    shapeText = "Shape placeholder - " + shapeText;
    canvasShapeType = MiroToCanvasShapeType.rectangle;
    bgColor = DEFAULT_BG_COLOR_UNKNOWN_WIDGET;
  }

  const nanoId = createElementId();
  const elementType = consts.CANVAS_ELEMENTS.SHAPE;

  miroCanvasMapper.push({ id: nanoId, miroId: miroId, miroIndex: miroIndex, miroFrameIndex: miroShape._parent?.index });

  const defaultSize = getDefaultSizeForShape(elementType, canvasShapeType);
  return {
    id: nanoId,
    type: elementType,
    element: {
      type: elementType,
      subtype: canvasShapeType,
      x: miroShape._position.offsetPx.x,
      y: miroShape._position.offsetPx.y,
      scaleX: (miroShape.scale.scale * miroShape.size.width) / defaultSize.width,
      scaleY: (miroShape.scale.scale * miroShape.size.height) / defaultSize.height,
      fill: bgColor,
      border: migrateFromDecimalColor(miroShapeStyle.brc, miroShapeStyle.bro),
      dash: Math.abs(miroShapeStyle.brs - 2),
      width: defaultSize.width,
      height: defaultSize.height,
      rotate: miroShape.rotation.rotation,
      radius: 100,
      attachedConnectors: {},
      strokeWidth: miroShapeStyle.brw,
      text: filterText(shapeText),
      link: getLinkInATag(shapeText),
      textColor: textColor,
      fontSize: miroShapeStyle.fs,
      align: MiroToCanvasTextAlign[miroShapeStyle.ta as keyof typeof MiroToCanvasTextAlign],
      fontProps: migrateFontProps(miroShapeStyle),
      font: getFont(miroShapeStyle.ffn),
    } as Shape,
  };
}

export function pasteFromMiro(clipboardData: string, mousePosition: MathUtils.Point) {
  const miroJson: MiroJson = getMiroJson(clipboardData);
  const canvasElements = migrateMiroJsonToCanvas(miroJson, mousePosition);
  const d = document.getElementById("miro-text-decypher");
  if (d) {
    document.body.removeChild(d);
  }
  return canvasElements;
}
